<template>
  <div class="notifications-page">
    <div class="page-header">
      <h1>Notifications</h1>
      <div class="header-actions">
        <button 
          v-if="unreadCount > 0" 
          @click="markAllAsRead" 
          class="btn btn-primary"
        >
          <i class="fas fa-check-double"></i> Mark All as Read
        </button>
      </div>
    </div>

    <!-- Connection Status -->
    <div v-if="!isConnected" class="connection-status alert alert-warning">
      <i class="fas fa-exclamation-triangle"></i>
      Real-time notifications disconnected. Attempting to reconnect...
    </div>

    <!-- Filters -->
    <div class="notification-filters">
      <button 
        @click="activeFilter = 'all'" 
        :class="{ active: activeFilter === 'all' }"
        class="filter-btn"
      >
        All ({{ allNotifications.length }})
      </button>
      <button 
        @click="activeFilter = 'unread'" 
        :class="{ active: activeFilter === 'unread' }"
        class="filter-btn"
      >
        Unread ({{ unreadCount }})
      </button>
    </div>

    <!-- Notifications List -->
    <div class="notifications-container">
      <!-- Loading state -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Loading notifications...
      </div>

      <!-- Empty state -->
      <div v-else-if="filteredNotifications.length === 0" class="empty-state">
        <i class="fas fa-inbox"></i>
        <h3>No notifications</h3>
        <p>{{ activeFilter === 'unread' ? 'You have no unread notifications' : 'You have no notifications yet' }}</p>
      </div>

      <!-- Notifications -->
      <div v-else class="notifications-list">
        <div 
          v-for="notification in filteredNotifications" 
          :key="notification.id"
          class="notification-card"
          :class="{ 'unread': notification.status === 'unread' }"
        >
          <div class="notification-icon" :class="`type-${notification.notification_type}`">
            <i :class="getNotificationIcon(notification.notification_type)"></i>
          </div>
          
          <div class="notification-body">
            <div class="notification-header-row">
              <div class="notification-title">{{ notification.title }}</div>
              <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
            </div>
            
            <div class="notification-message">{{ notification.message }}</div>
            
            <div v-if="notification.sender_name" class="notification-sender">
              From: {{ notification.sender_name }}
            </div>
            
            <div class="notification-actions">
              <button 
                v-if="notification.status === 'unread'" 
                @click="markAsRead(notification.id)" 
                class="btn-action"
              >
                <i class="fas fa-check"></i> Mark as read
              </button>
              <button 
                @click="deleteNotification(notification.id)" 
                class="btn-action btn-danger"
              >
                <i class="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'NotificationsPage',
  data() {
    return {
      activeFilter: 'all'
    }
  },
  computed: {
    ...mapGetters('Notifications', [
      'allNotifications',
      'unreadNotifications',
      'unreadCount',
      'loading',
      'isConnected'
    ]),
    filteredNotifications() {
      if (this.activeFilter === 'unread') {
        return this.unreadNotifications
      }
      return this.allNotifications
    }
  },
  methods: {
    ...mapActions('Notifications', [
      'markAsRead',
      'markAllAsRead',
      'deleteNotification',
      'fetchNotifications'
    ]),
    getNotificationIcon(type) {
      const icons = {
        info: 'fas fa-info-circle',
        success: 'fas fa-check-circle',
        warning: 'fas fa-exclamation-triangle',
        error: 'fas fa-exclamation-circle'
      }
      return icons[type] || icons.info
    },
    formatTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} minutes ago`
      if (diffHours < 24) return `${diffHours} hours ago`
      if (diffDays < 7) return `${diffDays} days ago`
      
      return date.toLocaleString()
    }
  },
  mounted() {
    this.fetchNotifications()
  }
}
</script>

<style scoped>
.notifications-page {
  padding: 24px;
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.connection-status {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.alert-warning {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.notification-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e2e8f0;
}

.filter-btn {
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
}

.filter-btn:hover {
  color: #3b82f6;
}

.filter-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
}

.notifications-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state,
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #94a3b8;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
  color: #cbd5e1;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 8px 0;
}

.notifications-list {
  padding: 16px;
}

.notification-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}

.notification-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.notification-card.unread {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.notification-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.notification-icon.type-info {
  background: #dbeafe;
  color: #3b82f6;
}

.notification-icon.type-success {
  background: #dcfce7;
  color: #22c55e;
}

.notification-icon.type-warning {
  background: #fef3c7;
  color: #f59e0b;
}

.notification-icon.type-error {
  background: #fee2e2;
  color: #ef4444;
}

.notification-body {
  flex: 1;
  min-width: 0;
}

.notification-header-row {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
}

.notification-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.notification-time {
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
}

.notification-message {
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 8px;
}

.notification-sender {
  font-size: 13px;
  color: #94a3b8;
  margin-bottom: 12px;
}

.notification-actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  background: transparent;
  border: 1px solid #e2e8f0;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-action:hover {
  background: white;
  border-color: #cbd5e1;
  color: #3b82f6;
}

.btn-action.btn-danger:hover {
  color: #ef4444;
  border-color: #fecaca;
}
</style>
